'use client';

import { forwardRef, useImperativeHandle, useState, useCallback, useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { useShallow } from 'zustand/react/shallow';
import { UnifiedImageUpload, type UnifiedImageUploadRef } from '@/shared/ui';
import { UnifiedUploadModal } from './UnifiedUploadModal';
import { useUploadStore } from '@/features/upload';
import { useCheckLimit, useSubscriptionLimits } from '@/app/model/stores';
import { cacheInvalidation } from '@/shared/lib/cache-invalidation';
import {
  trackEvent,
  AnalyticsEvent,
  ErrorType,
} from '@/shared/lib/analytics';
import { ApiClientError } from '@/shared/lib/api';
import { localizeUploadError } from '@/shared/lib/upload-error';
import { ConceptAngleType } from '@/shared/constants';
import type { AngleStoreResponse } from '@/types/concept';
import type { ProcessedFile } from '@/shared/lib/services/image-upload.service';
import type { ValidationItem } from './upload';
import type { UploadAngle } from '@/types/upload';
import type { ConceptAngleTypeType } from '@/shared/constants';
import type {
  MediaSourceExtraOption,
  MediaSourceProjectAngle,
} from '@/shared/ui/MediaSourceSelector';
import { ClassTypes } from '@/shared/constants/class-types';
import { registerAngleIntake } from '../lib/register-angle-intake';
import { createLogger } from '@/shared/lib/logger';
import { uploadsService } from '@/shared/api/services/uploads.service';
import { useLimitErrorPaywall } from '@/shared/hooks';
import { toast } from '@/shared/lib/toast';
import {
  setMutationLock,
  clearMutationLock,
} from '@/shared/api/websocket/realtime-sync.locks';

const logger = createLogger('AngleUpload');

interface AngleUploadProps {
  conceptId?: string;
  /**
   * The building the files enter. Required: an intake is building-scoped
   * (docs/upload/ADR-002). `conceptId` on top of it only says where a view
   * would land IF the server decides the file is one.
   */
  projectId: string;
  onSuccess: (angles: AngleStoreResponse | AngleStoreResponse[]) => void;
  onError?: (error: string) => void;
  onCancel?: () => void;
  className?: string;
  children?: React.ReactNode;
  onLimitReached?: () => void;
  currentAngleCount?: number;
  /**
   * Pre-flight check for the per-project angle library limit
   * (plan.limit_project_angles). When set and the count is >= the limit,
   * uploading/replacing is blocked with onProjectAngleLibraryLimitReached.
   * Replacement still counts against this limit because the server stores
   * each upload as a new angle record in the project library.
   */
  currentProjectAngleLibraryCount?: number;
  onProjectAngleLibraryLimitReached?: () => void;
  allowMultiple?: boolean;
  showValidation?: boolean;
  angleType?: ConceptAngleTypeType;
  uploadTitle?: string;
  uploadDescription?: string;
  mode?: 'create' | 'replace';
  replaceConceptAngleId?: string;
  /** Additional options for MediaSourceSelector (e.g. "Select from library") */
  mediaSourceExtraOptions?: MediaSourceExtraOption[];
  /** Optional gallery of project angles shown in the MediaSourceSelector. */
  mediaSourceProjectAngles?: MediaSourceProjectAngle[];
  /** Callback when an existing project angle thumbnail is tapped. */
  onMediaSourceProjectAngleSelected?: (angleId: string) => void;
  /** Auto-open file dialog on mount */
  autoOpen?: boolean;
  /**
   * Documents (PDF/txt/md) picked in the SAME mixed batch as the images. The
   * caller (Brief unified intake) uploads them via its own reference flow and
   * passes their live review rows here so they appear in the post-upload modal
   * alongside the images instead of being silently filed. Image-only or
   * docs-only batches leave this empty.
   */
  documentItems?: ValidationItem[];
  /** See UnifiedUploadModal: offered when a batch leaves the building view-less. */
  onAddAnotherPhoto?: () => void;
}

export interface AngleUploadRef {
  openFileDialog: () => void;
  /** Inject pre-selected image files into the same crop→upload→validation
   *  pipeline the file dialog uses (used by the Brief unified intake). */
  processFiles: (files: File[]) => void;
}

export const AngleUpload = forwardRef<AngleUploadRef, AngleUploadProps>(function AngleUpload({
  conceptId,
  projectId,
  onSuccess,
  onError,
  onCancel,
  className = '',
  children,
  onLimitReached,
  currentAngleCount = 0,
  currentProjectAngleLibraryCount,
  onProjectAngleLibraryLimitReached,
  allowMultiple = false,
  showValidation = true,
  angleType,
  uploadTitle,
  uploadDescription,
  mode = 'create',
  replaceConceptAngleId,
  mediaSourceExtraOptions,
  mediaSourceProjectAngles,
  onMediaSourceProjectAngleSelected,
  autoOpen = false,
  documentItems,
  onAddAnotherPhoto,
}, forwardedRef) {
  // STRICT add-view intake: a concept-scoped *create* (the "+" next to Main /
  // Second view) is the "add a view to this concept" entry point. The server
  // must NOT auto-route a different-building image to References here (that's the
  // lenient Brief behaviour) — it hard-blocks so the user confirms "It's my
  // building" or cancels. We signal this with `intent: 'view'` on create and a
  // `strict` flag to the result modal. Replace (different endpoint) and the
  // project-library Brief path (no conceptId) stay lenient.
  const isStrictViewIntake = !!conceptId && mode === 'create';
  const uploadRef = useRef<UnifiedImageUploadRef>(null);
  const [uploadedAngles, setUploadedAngles] = useState<UploadAngle[]>([]);
  const [uploadPhase, setUploadPhase] = useState<
    'idle' | 'uploading' | 'validating' | 'complete'
  >('idle');
  const [showModal, setShowModal] = useState(false);
  const [uploadProgressData, setUploadProgressData] = useState<
    { percentage: number; currentFile: number; totalFiles: number } | undefined
  >();
  // Local object URLs of the in-flight files — per-file rows in the progress
  // modal during the uploading phase. Ownership transfers to
  // UploadAngle.previewUrl on success (revoked via the effect below); on
  // error/unmount the ref still holds them and they're revoked here.
  const [localPreviews, setLocalPreviews] = useState<string[]>([]);
  const pendingPreviewUrlsRef = useRef<string[]>([]);

  useEffect(() => {
    return () => {
      pendingPreviewUrlsRef.current.forEach((url) =>
        URL.revokeObjectURL(url)
      );
    };
  }, []);

  // Revoke local thumbnail object URLs when the batch is replaced/cleared
  // (incl. unmount) — each UploadAngle.previewUrl is created in handleUpload.
  useEffect(() => {
    return () => {
      uploadedAngles.forEach((angle) => {
        if (angle.previewUrl) URL.revokeObjectURL(angle.previewUrl);
      });
    };
  }, [uploadedAngles]);

  const checkLimit = useCheckLimit();
  const limits = useSubscriptionLimits();
  const t = useTranslations('angles');
  const tUpload = useTranslations('fileUpload');

  // Server-driven paywall routing for typed 422 limit codes. The project-
  // library path preserves the existing preflight-drift warning log; the
  // concept-angle path reuses the in-concept upgrade surface already wired
  // via `onLimitReached` (both the main-angle modal and the derivative flow
  // open `showUpgradePrompt` here).
  const resetUploadUiOnLimit = useCallback(() => {
    setUploadPhase('idle');
    setShowModal(false);
  }, []);

  const routeLimitErrorToPaywall = useLimitErrorPaywall({
    onAngleLibraryLimit: () => {
      if (currentProjectAngleLibraryCount !== undefined) {
        logger.warn(
          'AngleUpload preflight missed: server rejected but cache present'
        );
      }
      resetUploadUiOnLimit();
      onProjectAngleLibraryLimitReached?.();
    },
    onConceptAngleLimit: () => {
      resetUploadUiOnLimit();
      onLimitReached?.();
    },
  });

  // Upload store state and actions
  const { createBatch, uploadBatch, uploadSingle } = useUploadStore();

  // Real upload progress aggregated from the store (XHR byte progress flows
  // into item.progress via upload-recovery → image-upload.service). Undefined
  // while no upload is in flight.
  const storeProgress = useUploadStore(
    useShallow(
      (
        state
      ):
        | { percentage: number; currentFile: number; totalFiles: number }
        | undefined => {
        if (!state.ui.loading.upload) return undefined;
        const batchId = state.ui.currentBatchId;
        const batch = batchId ? state.batches[batchId] : null;
        if (batch && batch.items.length > 0) {
          const completed = batch.items.filter(
            (item) => item.status === 'completed'
          ).length;
          const percentage = Math.round(
            batch.items.reduce((sum, item) => sum + item.progress, 0) /
              batch.items.length
          );
          return {
            percentage,
            currentFile: Math.min(completed + 1, batch.items.length),
            totalFiles: batch.items.length,
          };
        }
        const item = state.queue[0];
        return { percentage: item?.progress ?? 0, currentFile: 1, totalFiles: 1 };
      }
    )
  );

  // Mirror store progress into local state so the last real value is retained
  // during the uploading→validating transition (the store flips loading off
  // before the phase state updates — clearing here would flash an empty bar).
  useEffect(() => {
    if (storeProgress) {
      setUploadProgressData(storeProgress);
    }
  }, [storeProgress]);

  // One intake per file: register it against the building and get a presigned
  // PUT back. Where a VIEW would land (this concept, this slot, replacing this
  // one) travels with the registration; whether the file IS a view is the
  // server's answer, delivered later on the intake itself (ADR-002).
  const createAnglesApi = useCallback(
    async (processedFiles: ProcessedFile[]) => {
      // One create round trip per file — run them in PARALLEL. A sequential
      // loop added N×RTT of dead time between crop submit and the first
      // uploaded byte (matches Android UploadPipeline behavior).
      return Promise.all(
        processedFiles
          .filter((file): file is ProcessedFile => !!file)
          .map((file) =>
            registerAngleIntake(projectId, file, {
              // STRICT add-view intake — the server refuses a non-view instead
              // of filing it away. Absent means "classify and sort".
              ...(isStrictViewIntake ? { intent: 'view' as const } : {}),
              ...(conceptId ? { concept_id: conceptId } : {}),
              ...(angleType ? { angle_type: angleType } : {}),
              ...(mode === 'replace' && replaceConceptAngleId
                ? { replace_concept_angle_id: replaceConceptAngleId }
                : {}),
            })
          )
      );
    },
    [
      projectId,
      conceptId,
      angleType,
      mode,
      replaceConceptAngleId,
      isStrictViewIntake,
    ]
  );

  // Derivative ("add a second view") vs main — pairs with the angle_kind on
  // `angle_upload_completed` (main fired from useCreateConcept). Project-library
  // and concept-creation uploads default to "main".
  const angleKind =
    angleType === ConceptAngleType.DERIVATIVE ? 'derivative' : 'main';

  const handleUpload = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;
      // Upload INTENT — the counterpart to angle_upload_completed. Fired once we
      // genuinely start uploading picked files (source is a generic OS picker on
      // web, so "unknown"). Cross-platform contract; see analytics.ts.
      trackEvent(AnalyticsEvent.ANGLE_UPLOAD_STARTED, {
        angle_kind: angleKind,
        source: 'unknown',
      });
      // Lock angles entity to prevent WebSocket-triggered concept refetch
      // before the uploaded file is available on CDN (prevents negative caching)
      setMutationLock('angles', 30_000);
      try {
        // Show modal and set uploading phase. Seed honest initial progress —
        // real byte progress streams in via the store selector above.
        setShowModal(true);
        setUploadPhase('uploading');
        setUploadProgressData({
          percentage: 0,
          currentFile: 1,
          totalFiles: files.length,
        });
        const previewUrls = files.map((file) => URL.createObjectURL(file));
        pendingPreviewUrlsRef.current = previewUrls;
        setLocalPreviews(previewUrls);

        // Confirm runs after S3 success for every target — the server
        // HeadObject-checks R2, marks the angle confirmed and only then
        // starts validation / emits the `angles/created` realtime event.
        const confirm = async (id: string) => {
          await uploadsService.confirm(projectId, id);
        };

        let results;

        if (files.length === 1) {
          // Single file upload. The guard is unreachable (length === 1) but
          // must still clean up the modal — the finally only releases the lock.
          const file = files[0];
          if (!file) {
            setShowModal(false);
            setUploadPhase('idle');
            return;
          }

          const result = await uploadSingle(
            file,
            'angle',
            async (processedFile) => {
              const angles = await createAnglesApi([processedFile]);
              const uploadData = angles[0];
              if (!uploadData) {
                throw new Error('Failed to create angle');
              }
              return uploadData;
            },
            confirm
          );
          results = [result];
        } else {
          // Batch upload — `mainIndex` undefined: callers without conceptId go
          // through the concept-creation modal which owns the main-angle pick;
          // conceptId-bound callers don't need a main pick (single-angle path).
          const batchId = createBatch(files, 'angle');
          results = await uploadBatch(batchId, createAnglesApi, confirm);
        }

        // Library uploads must surface in the Views list immediately as
        // pending-validation rows — the WS validation event arrives much later.
        if (!conceptId && projectId) {
          cacheInvalidation.afterCreateProjectAngle(projectId);
        }

        // A file whose PUT failed is dropped from `results`, so the rows below
        // are indexed by the position the upload REMEMBERS, never by position in
        // the shrunken array — that shift put photo 1's thumbnail on photo 2's
        // row and showed each verdict under the wrong picture.
        const droppedCount = files.length - results.length;
        if (droppedCount > 0) {
          toast.error(
            t('uploadPartialFailed', {
              failed: droppedCount,
              total: files.length,
            })
          );
        }

        // One row per intake. `id` is the INTAKE, not a view: which of these
        // becomes a view is the server's answer, and the row learns its
        // `angleId` from the outcome when validation finishes.
        const angles: UploadAngle[] = results.map((result) => ({
          type: ClassTypes.ANGLE,
          id: result.id,
          previewUrl: previewUrls[result.index],
          attributes: {
            original_filename: files[result.index]?.name || 'Image',
            validation_performed_at: null,
            resized_file_name: '',
            resized_file_url: null,
            resized_file_width: 0,
            resized_file_height: 0,
            resized_file_size: 0,
            uploaded_at: new Date().toISOString(),
            is_main: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          },
        }));

        // A dropped file's preview has no row to own it any more.
        if (droppedCount > 0) {
          const claimed = new Set(results.map((result) => result.index));
          previewUrls.forEach((url, index) => {
            if (!claimed.has(index)) URL.revokeObjectURL(url);
          });
        }

        // Preview-URL ownership moves to uploadedAngles — its cleanup effect
        // revokes them when the batch is replaced/cleared.
        setUploadedAngles(angles);
        pendingPreviewUrlsRef.current = [];
        setLocalPreviews([]);

        if (showValidation) {
          // Transition to validation phase
          setUploadPhase('validating');
        } else {
          // No review window: the caller watches the intake itself and resolves
          // the view from its outcome, so these ids are INTAKE ids.
          setUploadPhase('complete');
          setShowModal(false);
          const angleResponses = results.map(
            (result) =>
              ({
                id: result.id,
                attributes: {},
              }) as AngleStoreResponse
          );
          const response =
            angleResponses.length === 1 ? angleResponses[0] : angleResponses;
          if (response) {
            onSuccess(response);
          }
        }

      } catch (error) {
        // Server routes both project-library and concept-angle paywalls via
        // typed 422 codes — delegate to the hook (same surfaces the
        // preflights use) and skip the generic error dialog for those cases.
        if (routeLimitErrorToPaywall(error)) {
          return;
        }
        // Real upload/processing failure (NOT a paywall route, which returned
        // above; NOT a plain picker cancel, which never reaches handleUpload).
        // Map to the shared ErrorType vocabulary.
        const errorType =
          error instanceof ApiClientError
            ? error.isNetworkError()
              ? ErrorType.NETWORK
              : error.isServerError()
                ? ErrorType.SERVER
                : error.isValidationError()
                  ? ErrorType.VALIDATION
                  : ErrorType.UNKNOWN
            : ErrorType.UNKNOWN;
        trackEvent(AnalyticsEvent.ANGLE_UPLOAD_FAILED, {
          angle_kind: angleKind,
          error_type: errorType,
        });
        logger.error('Upload failed:', error);
        // Fatal upload failures (422 duplicate md5 on replace, etc.) surface
        // as a toast + flow dismiss, not an inline error modal — the progress
        // dialog is meaningless once the flow is dead. Mirrors Android
        // MultiImageUploadViewModel.reportFatalFailure and Apple
        // MultiImageUploadManager.reportFatalFailure.
        // S3-layer failures carry a reason code, not English prose — localize
        // them; API errors keep the server's own translated detail.
        const message = localizeUploadError(error, tUpload, t('uploadFailed'));
        toast.error(message);
        pendingPreviewUrlsRef.current.forEach((url) =>
          URL.revokeObjectURL(url)
        );
        pendingPreviewUrlsRef.current = [];
        setLocalPreviews([]);
        setShowModal(false);
        setUploadedAngles([]);
        setUploadPhase('idle');
        if (onError) {
          onError(message);
        }
        onCancel?.();
      } finally {
        // Covers every exit (success, error, early return) — leaking the lock
        // would freeze WS-driven angle invalidation for its full 30s TTL.
        clearMutationLock('angles');
      }
    },
    [
      conceptId,
      projectId,
      angleKind,
      createAnglesApi,
      uploadSingle,
      createBatch,
      uploadBatch,
      showValidation,
      onSuccess,
      onError,
      onCancel,
      routeLimitErrorToPaywall,
      t,
      tUpload,
    ]
  );

  // Pre-flight gate: returning false vetoes the whole flow inside
  // UnifiedImageUpload (no crop queue, no upload), so a paywall never opens
  // underneath an active crop modal.
  const handleFilesSelected = useCallback(
    (files: File[]): boolean => {
      // Replace flow: server now permits replace even when the library is full
      // (it rewrites the existing slot), so no preflight — just let the server
      // decide. The catch-block maps ANGLE_LIBRARY_LIMIT_EXCEEDED to paywall.
      if (mode === 'replace') {
        return true;
      }

      // Add-flow project-library pre-flight. We need both the current count
      // AND the plan limit; if either is missing skip the preflight and rely
      // on the 422 ANGLE_LIBRARY_LIMIT_EXCEEDED surface instead of guessing
      // (a 0 default would spuriously open the paywall on a fresh page load
      // before subscription/state hydrates).
      if (onProjectAngleLibraryLimitReached) {
        const projectAngleLimit = limits?.project_angles;
        if (
          currentProjectAngleLibraryCount === undefined ||
          projectAngleLimit === undefined
        ) {
          logger.warn('AngleUpload preflight bypassed: missing cache');
        } else if (
          typeof projectAngleLimit === 'number' &&
          projectAngleLimit > 0 &&
          currentProjectAngleLibraryCount + files.length > projectAngleLimit
        ) {
          onProjectAngleLibraryLimitReached();
          return false;
        }
      }

      // Concept-angle preflight — only when the upload actually targets a
      // concept (the consumer wired onLimitReached). The project-library path
      // (Brief) passes no callback and the per-concept limit does not apply
      // there; running the check anyway used to veto the selection silently.
      // Unhydrated limits also skip the preflight (checkLimit returns false on
      // missing cache, which would veto every pick during the cold-start
      // window) — the server's 422 surface stays the source of truth.
      if (onLimitReached && limits) {
        const totalAngles = currentAngleCount + files.length;
        if (!checkLimit('angles', totalAngles - 1)) {
          onLimitReached();
          return false;
        }
      }

      return true;
    },
    [
      checkLimit,
      limits,
      currentAngleCount,
      currentProjectAngleLibraryCount,
      onLimitReached,
      onProjectAngleLibraryLimitReached,
      mode,
    ]
  );

  const handleFilesProcessed = useCallback(
    async (files: File[]) => {
      logger.info('Files processed (cropped):', files.length);
      // Multi-file flows without a conceptId hand the picked images straight
      // to ConceptAnglesEditModal (created post-upload) — that modal owns the
      // main-angle pick and reorder UX, so no pre-upload selector is needed
      // here. Keeps WEB in parity with Apple/Android.
      await handleUpload(files);
    },
    [handleUpload]
  );

  const handleUploadError = useCallback(
    (error: Error) => {
      if (onError) {
        onError(localizeUploadError(error, tUpload, t('uploadFailed')));
      }
    },
    [onError, t, tUpload]
  );

  const handleValidationComplete = useCallback(
    (validAngles: UploadAngle[]) => {
      // Mark as complete and close modal
      setUploadPhase('complete');
      setShowModal(false);

      // Report the VIEWS the intakes produced — what every consumer goes on to
      // attach, render or open. An accepted row always names one.
      const angleResponses = validAngles
        .filter((angle) => !!angle.angleId)
        .map(
          (angle) =>
            ({
              id: angle.angleId as string,
              attributes: {},
            }) as AngleStoreResponse
        );

      const response =
        angleResponses.length === 1 ? angleResponses[0] : angleResponses;
      if (response) {
        onSuccess(response);
      }
      setUploadedAngles([]);
      setUploadPhase('idle');
    },
    [onSuccess]
  );

  const handleModalClose = useCallback(() => {
    setShowModal(false);
    setUploadedAngles([]);
    setUploadPhase('idle');
  }, []);

  // Auto-open MediaSourceSelector on mount
  useEffect(() => {
    if (autoOpen) {
      // Small delay to ensure refs are attached
      const timer = setTimeout(() => {
        uploadRef.current?.openFileDialog();
      }, 100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [autoOpen]);

  useImperativeHandle(
    forwardedRef,
    () => ({
      openFileDialog: () => uploadRef.current?.openFileDialog(),
      processFiles: (files: File[]) => uploadRef.current?.processFiles(files),
    }),
    []
  );

  return (
    <>
      <UnifiedImageUpload
        ref={uploadRef}
        uploadType="angle"
        mode={mode}
        conceptId={conceptId}
        allowMultiple={allowMultiple}
        maxFiles={allowMultiple ? 10 : 1}
        allowCrop={true}
        freeAspectRatio={true}
        showRendersOption={true}
        mediaSourceExtraOptions={mediaSourceExtraOptions}
        mediaSourceProjectAngles={mediaSourceProjectAngles}
        onMediaSourceProjectAngleSelected={onMediaSourceProjectAngleSelected}
        // Picker abandoned (sheet dismissed / file dialog cancelled) = flow
        // cancelled. autoOpen owners (add view, replace angle) unmount this
        // component in onCancel — without the signal their `open` flag stays
        // stuck and the sheet never reopens on the next tap.
        onMediaSourceDismiss={onCancel}
        className={className}
        onFilesSelected={handleFilesSelected}
        onFilesProcessed={handleFilesProcessed}
        onUploadError={handleUploadError}
        uploadTitle={uploadTitle}
        uploadDescription={uploadDescription}
      >
        {children}
      </UnifiedImageUpload>

      <UnifiedUploadModal
        isOpen={showModal}
        phase={uploadPhase}
        uploadProgress={uploadProgressData}
        localPreviews={localPreviews}
        angles={uploadedAngles}
        documentItems={documentItems}
        projectId={projectId}
        onClose={handleModalClose}
        onCancel={onCancel}
        onComplete={handleValidationComplete}
        onAddAnotherPhoto={onAddAnotherPhoto}
      />
    </>
  );
});
